import { useEffect, useRef } from 'react';
import { useConfig } from '@browsing/config';
import { tracker } from '../track';
import { trackViewPromotion } from '../events';
const TRACK_INTERVAL_MS = 10000;
const TRACK_IMPRESSION_LIMIT = 15;
const TRACKING_QUERY = '[id^="open-Z"],[id^="thb-Z"][aria-hidden="false"],[id^="upcoming-Z"]';
const isRectIntersecting = (intersectionRect) => intersectionRect.height > 0 && intersectionRect.width > 0;
const getTrackingObject = (element, categoryTabId, visitorID) => {
    const elementbyId = element.getAttribute('id');
    if (elementbyId) {
        const elementIdArray = elementbyId.split('-');
        const source = elementIdArray[0];
        const id = elementIdArray[1];
        const isGetInspired = elementIdArray.length === 3 && elementIdArray[2] === 'isGetInspired';
        return {
            id,
            name: source,
            position: element.getAttribute('data-position'),
            creative: `${categoryTabId}|campaignOverview`,
            visitorID,
            dimension113: isGetInspired ? '1' : '0',
        };
    }
    return {};
};
/**
 * TODO: This function should be generic and allow to observe any element.
 */
export const useTrackCampaignsImpression = ({ categoryTabId, isMobileScreen, visitorID, }) => {
    const applicationConfig = useConfig();
    const impressionsRef = useRef([]); // use ref to persist data and avoid rerender
    const ga4OpenCampaignsImpressionsRef = useRef([]);
    const ga4UpcomingCampaignsImpressionsRef = useRef([]);
    const ga4ThbCampaignsImpressionsRef = useRef([]);
    const getCampaignImpressionData = (impressions) => {
        const trackingData = {
            ecommerce: {
                promoView: {
                    promotions: [...impressions],
                },
            },
        };
        return trackingData;
    };
    useEffect(() => {
        const trackingCallback = () => {
            if (impressionsRef.current.length) {
                tracker.track('view_item_list', getCampaignImpressionData(impressionsRef.current));
                impressionsRef.current = [];
            }
            if (applicationConfig.isEnabled('ga4')) {
                try {
                    if (ga4OpenCampaignsImpressionsRef.current.length) {
                        trackViewPromotion({
                            creativeName: 'open',
                            itemListName: 'open',
                            items: ga4OpenCampaignsImpressionsRef.current.map(item => {
                                var _a, _b;
                                const index = (_a = item.position) === null || _a === void 0 ? void 0 : _a.split('|')[0];
                                const categoryId = (_b = item.creative) === null || _b === void 0 ? void 0 : _b.split('|')[0];
                                return {
                                    campaignIdentifier: item.id,
                                    categoryId,
                                    index: isMobileScreen ? Number(index) : Number(index) - 1,
                                };
                            }),
                        });
                        ga4OpenCampaignsImpressionsRef.current = [];
                    }
                    if (ga4UpcomingCampaignsImpressionsRef.current.length) {
                        trackViewPromotion({
                            element: 'upcoming',
                            creativeName: 'upcoming',
                            itemListName: 'upcoming',
                            items: ga4UpcomingCampaignsImpressionsRef.current.map(item => ({
                                campaignIdentifier: item.id,
                                index: Number(item.position) - 1,
                            })),
                        });
                        ga4UpcomingCampaignsImpressionsRef.current = [];
                    }
                    if (ga4ThbCampaignsImpressionsRef.current.length) {
                        trackViewPromotion({
                            element: 'thb',
                            creativeName: 'thb',
                            itemListName: 'thb',
                            items: ga4ThbCampaignsImpressionsRef.current.map(item => {
                                var _a;
                                const categoryId = (_a = item.creative) === null || _a === void 0 ? void 0 : _a.split('|')[0];
                                return {
                                    campaignIdentifier: item.id,
                                    categoryId,
                                    index: Number(item.position) - 1,
                                };
                            }),
                        });
                        ga4ThbCampaignsImpressionsRef.current = [];
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('[@browsing/tracker] Tracking `view_promotion` failed.', e);
                }
            }
        };
        // Create IntersectionObserver to track campaign impressions
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting && isRectIntersecting(entry.intersectionRect)) {
                const campaign = getTrackingObject(entry.target, categoryTabId, visitorID);
                impressionsRef.current = [...impressionsRef.current, campaign];
                switch (campaign.name) {
                    case 'open': {
                        ga4OpenCampaignsImpressionsRef.current = [
                            ...ga4OpenCampaignsImpressionsRef.current,
                            campaign,
                        ];
                        break;
                    }
                    case 'upcoming': {
                        ga4UpcomingCampaignsImpressionsRef.current = [
                            ...ga4UpcomingCampaignsImpressionsRef.current,
                            campaign,
                        ];
                        break;
                    }
                    case 'thb': {
                        ga4ThbCampaignsImpressionsRef.current = [
                            ...ga4ThbCampaignsImpressionsRef.current,
                            campaign,
                        ];
                        break;
                    }
                    default: {
                        break;
                    }
                }
                observer.unobserve(entry.target);
            }
            if (impressionsRef.current.length === TRACK_IMPRESSION_LIMIT) {
                trackingCallback();
            }
        }), {
            root: null,
            rootMargin: '0px',
            threshold: 1.0, // trigger if 100% in viewport
        });
        // throttle impression sending
        const intervalId = setInterval(trackingCallback, TRACK_INTERVAL_MS);
        window.addEventListener('beforeunload', trackingCallback);
        // give time to animation to complete
        const animateTimeoutId = setTimeout(() => {
            const campaignElements = document.querySelectorAll(TRACKING_QUERY);
            // start observing nodes
            Array.from(campaignElements).forEach(el => observer.observe(el));
        }, 600);
        // upcoming section now is lazy-loadable, so track lazy loading we use Mutation Observer and add them to Intersection Obs. list
        let upcomingAnimateTimeoutId;
        const config = {
            childList: true,
            subtree: true,
        };
        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mut => {
                Array.from(mut.addedNodes).forEach((value) => {
                    // after upcoming section is added, we need to start observing campaigns inside
                    const node = value.firstChild;
                    if (node) {
                        if (node.id === 'upcoming-campaigns' ||
                            value.id === 'upcoming-campaigns') {
                            upcomingAnimateTimeoutId = setTimeout(() => {
                                const upcomingCampaignElements = node.querySelectorAll(TRACKING_QUERY);
                                // start observing nodes
                                Array.from(upcomingCampaignElements).forEach(el => observer.observe(el));
                            }, 100);
                        }
                        // track lazy loaded open campaigns on mobile
                        if (node.id && node.id.startsWith('open-Z')) {
                            // console.log('add tracking for node', node.id);
                            observer.observe(node);
                        }
                    }
                });
            });
        });
        // give animations a little bit time, might be changed with RAF
        const mutationObserverTimeoutId = setTimeout(() => {
            // in mobile we use lazy load for campaigns
            const openCampaignQuerySelector = document.querySelector('#campaigns-open');
            if (isMobileScreen && openCampaignQuerySelector) {
                mutationObserver.observe(openCampaignQuerySelector, config);
            }
            const upcomingCampaignsQuerySelector = document.querySelector('#lazyload-campaigns-upcoming');
            if (upcomingCampaignsQuerySelector) {
                mutationObserver.observe(upcomingCampaignsQuerySelector, config);
            }
        }, 500);
        return () => {
            if (upcomingAnimateTimeoutId) {
                clearTimeout(upcomingAnimateTimeoutId);
            }
            clearTimeout(animateTimeoutId);
            clearInterval(intervalId);
            observer.disconnect();
            window.removeEventListener('beforeunload', trackingCallback);
            if (mutationObserver) {
                mutationObserver.disconnect();
                clearTimeout(mutationObserverTimeoutId);
            }
        };
    }, [applicationConfig, visitorID, categoryTabId, isMobileScreen]);
};
